import { Component, Inject, Input } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import saveAs from 'save-as';

import { DATE_FORMATS } from '@shared/consts/date-formats';
import { MESSAGES } from '@shared/consts/messages';
import { ErrorHelper } from '@shared/helpers/error.helper';
import { MessageHelper } from '@shared/helpers/message.helper';
import { IFile } from '@shared/models/interfaces/IFile';
import { IFilesModalDialogData } from '@shared/models/interfaces/IFilesModalDialogData';
import { VesselService } from '@shared/services/vessel.service';
import { HttpClient } from '@angular/common/http';
import { TableauDialogComponent } from '@shared/components/tableau-dialog/tableau-dialog.component';
import { TableauService } from '@shared/services/tableau.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { MixpanelHelper } from '@shared/helpers/mixpanel.helper';


@Component({
  templateUrl: './files-modal.component.html',
  styleUrls: ['./files-modal.component.scss'],
})
export class FilesModalComponent {
  private id: string;
  private downloading: number[] = [];
  private allFiles: IFile[] = [];

  DATE_FORMATS = DATE_FORMATS;

  list: IFile[] = [];
  loading: boolean;
  filterActive: string;

  @Input() isMedia: boolean;
  @Input() title: string;
  @Input() filters: string[];

  constructor(
    private errorHelper: ErrorHelper,
    private vesselService: VesselService,
    private http: HttpClient,
    private messageHelper: MessageHelper,
    private mixpanelHelper: MixpanelHelper,
    public dialogRef: MatDialogRef<FilesModalComponent>,
    public dialog: MatDialog,
    private tableauService: TableauService,
    private route: ActivatedRoute, 
    @Inject(MAT_DIALOG_DATA) data: IFilesModalDialogData
  ) {
    this.id = data.vesselId;
    this.allFiles = [...data.files];
    this.list = [...data.files];
    this.isMedia = data.isMedia;
    this.title = data.title;
    this.filters = data.filters ?? [];

    if (this.isMedia) {
      this.filters = ['All', 'Photos', 'Videos'];
    }

    if (this.filters.length) {
      this.filterActive = this.filters[0];
    }

    this.allFiles = this.allFiles.sort((a, b) => {
      if (a.type === "Dashboard" && b.type !== "Dashboard") {
        return -1; // Move 'a' to an earlier position
      } else if (a.type !== "Dashboard" && b.type === "Dashboard") {
        return 1; // Move 'b' to an earlier position
      } else {
        return 0; // Keep original order if same type
      }
    });
    this.route.queryParams.subscribe(params => {
      const tableauId = params['dashboard'];
      if (tableauId && this.allFiles) {
        const tableauItem = this.allFiles.find(function(item) {
          return item.id == tableauId;
        });
        if (tableauItem) {
          this.openTableauDialog(tableauItem);
        }
      }
    });
  }

  getIcon(type: string) {
    switch (type) {
      case 'Dashboard':
        return 'bar_chart';
      case 'Photo':
        return 'image';
      case 'Video':
        return 'smart_display';
      case 'Document':
        return 'description';
      default:
        return 'link';
    }
  }

  handleFilter(filter: string) {
    this.filterActive = filter;
    if (filter === 'All') {
      this.list = this.allFiles;
    } else {
      const type = filter.endsWith('s') ? filter.slice(0, -1) : filter;
      const filtered = this.allFiles.filter((x) => x.type === type);
      this.list = filtered;
    }
  }

  public async getToken() {
    const vizToken = await this.tableauService.get();
    return vizToken.token;
  }

  async openTableauDialog(item: IFile) {
    this.getToken().then((value) => {
      let tableauHtml = `<tableau-viz
      id="tableau-viz"
      src="${item.url}"
      token="${value}"
      width="100%"
      height="70vh"
      hide-tabs
      toolbar="bottom"
    >
    </tableau-viz>
      `;

      this.mixpanelHelper.reportDetailVisualization(item.name, item.url);

      this.dialog.open(TableauDialogComponent, {
        panelClass: 'custom-dialog-container',
        data: {
          tableauId: item.id,
          title: item.name,
          message: tableauHtml,
        },
        width: '1200px',
      });
    });
  }

  open(item: IFile) {
    var link = document.createElement('a');

    this.mixpanelHelper.reportDetailVisualization(item.name, item.url);

    link.setAttribute('href', item.url);
    link.setAttribute('target', '_blank');

    link.click();
  }

  async download(item: IFile) {
    this.messageHelper.send(MESSAGES.downloading);

    if (this.downloading.includes(item.id)) return;

    try {
      this.downloading.push(item.id);
      const blob = await this.downloadMethod(item.id);
      saveAs(blob, `${item.name}.${item.extension}`.toLowerCase());
    } catch (error) {
      this.errorHelper.catch(error);
    } finally {
      this.downloading = this.downloading.filter((id) => id !== item.id);
      this.mixpanelHelper.reportDetailVisualization(
        `${item.name}.${item.extension}`
      );
    }
  }

  handleItemClick(item: IFile): void {
    if (item.type === 'Link') {
      this.open(item);
    } else if (item.type === 'Dashboard') {
      this.openTableauDialog(item);
    } else {
      this.download(item);
    }
  }

  private downloadMethod(fileId: number) {
    if (this.id) {
      if (this.isMedia) {
        return this.vesselService.downloadMedia(this.id, fileId);
      } else {
        return this.vesselService.downloadDocument(this.id, fileId);
      }
    } else {
      let file;
      try {
        file = this.allFiles.find(item => item.id === fileId);
      } catch (error) {
        this.errorHelper.catch(error);
      } finally {
        return this.http
        .get(file.url, {
          responseType: 'blob',
        })
        .toPromise();
      }
    }
  }
}

